import { useEffect, useState } from 'react';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import adminService from '../../services/admin.service';
import type {
  MarginResponse,
  PerCallCostStatsResponse,
} from '../../types/admin.types';
import DateRangePicker, { DateRange } from '../shared/DateRangePicker';
import StatCard from '../shared/StatCard';

interface CostToServePanelProps {
  userId: string;
}

type Tab = 'distribution' | 'margin';

const PERIOD_OPTIONS = [
  { value: 'last_7d', label: '7 days' },
  { value: 'last_30d', label: '30 days' },
  { value: 'last_90d', label: '90 days' },
];

function defaultRange(): DateRange {
  const now = new Date();
  const from = new Date(now.getTime() - 30 * 86_400_000);
  return { from: from.toISOString(), to: now.toISOString() };
}

function formatCost(cost: string | number, decimals?: number): string {
  const num = typeof cost === 'string' ? parseFloat(cost) : cost;
  if (isNaN(num)) return '$0.00';
  // Auto-select precision: use enough decimals to show the first significant digit
  if (decimals === undefined) {
    if (num === 0) return '$0.0000';
    const abs = Math.abs(num);
    if (abs >= 0.01) decimals = 4;
    else if (abs >= 0.0001) decimals = 6;
    else decimals = 8;
  }
  return `$${num.toFixed(decimals)}`;
}

export default function CostToServePanel({ userId }: CostToServePanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>('distribution');
  const [period, setPeriod] = useState('last_30d');
  const [range, setRange] = useState<DateRange>(() => defaultRange());
  const [distribution, setDistribution] = useState<PerCallCostStatsResponse | null>(null);
  const [margin, setMargin] = useState<MarginResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeTab, period, range.from, range.to]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'distribution') {
        const data = await adminService.getPerCallCostStats(userId, range);
        setDistribution(data);
      } else if (activeTab === 'margin') {
        const data = await adminService.getCustomerMargin(userId, period);
        setMargin(data);
      }
    } catch (err) {
      setError('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'distribution', label: 'Distribution', icon: Squares2X2Icon },
    { key: 'margin', label: 'Margin', icon: CurrencyDollarIcon },
  ];

  return (
    <div className="rounded-xl bg-white shadow">
      <div className="border-b border-gray-200 px-6 pt-5 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Cost-to-Serve</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Per-call cost distribution and customer margin, computed from actual recorded
              spend. Per-call detail is on each analysis page.
            </p>
          </div>
          {activeTab === 'distribution' && (
            <DateRangePicker value={range} onChange={setRange} />
          )}
          {activeTab === 'margin' && (
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="rounded-md border-gray-300 text-sm"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <nav className="-mb-px flex space-x-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 border-b-2 pb-3 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="p-6">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {!loading && !error && activeTab === 'distribution' && distribution && (
          <DistributionTab data={distribution} />
        )}
        {!loading && !error && activeTab === 'margin' && margin && (
          <MarginTab margin={margin} />
        )}
      </div>
    </div>
  );
}

function DistributionTab({ data }: { data: PerCallCostStatsResponse }) {
  const [showCapability, setShowCapability] = useState(false);

  if (data.count === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <ChartBarIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm">No cost data for this range.</p>
        <p className="text-xs text-gray-400 mt-1">
          Cost records are created as analyses complete.
        </p>
      </div>
    );
  }

  const stats = data.total_cost_stats!;
  const histogramData = data.histogram.map((b, i) => ({
    label: `$${b.bucket_start.toFixed(4)}`,
    count: b.count,
    bucket: i,
    range: `$${b.bucket_start.toFixed(4)} – $${b.bucket_end.toFixed(4)}`,
  }));
  const totalMinutes = data.total_minutes ? parseFloat(data.total_minutes) : 0;

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
        <StatCard label="Calls" value={data.count.toLocaleString()} />
        <StatCard label="Min" value={formatCost(stats.min ?? '0')} />
        <StatCard label="P25" value={formatCost(stats.p25 ?? '0')} />
        <StatCard label="Median" value={formatCost(stats.median ?? '0')} emphasis />
        <StatCard label="Avg" value={formatCost(stats.avg ?? '0')} />
        <StatCard label="P75" value={formatCost(stats.p75 ?? '0')} />
        <StatCard label="P95" value={formatCost(stats.p95 ?? '0')} />
        <StatCard label="Max" value={formatCost(stats.max ?? '0')} />
        <StatCard label="Stddev" value={formatCost(stats.stddev ?? '0')} />
      </div>

      {/* Total summary */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-gray-50 px-4 py-3 text-sm">
        <div>
          <span className="text-gray-500">Total cost in range:</span>{' '}
          <span className="font-mono font-semibold text-gray-900">{formatCost(stats.sum ?? '0', 2)}</span>
        </div>
        {totalMinutes > 0 && (
          <div>
            <span className="text-gray-500">Minutes processed:</span>{' '}
            <span className="font-mono font-semibold text-gray-900">{totalMinutes.toFixed(1)}</span>
          </div>
        )}
        {data.cost_per_minute_stats && (
          <div>
            <span className="text-gray-500">Median $/min:</span>{' '}
            <span className="font-mono font-semibold text-gray-900">
              {formatCost(data.cost_per_minute_stats.median ?? '0')}
            </span>
          </div>
        )}
      </div>

      {/* Histogram */}
      <div className="rounded-lg border border-gray-200 p-3">
        <h4 className="mb-2 text-sm font-medium text-gray-700">Per-call cost distribution</h4>
        <div style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer>
            <BarChart data={histogramData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={45}
              />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <RTooltip
                content={({ active, payload }) =>
                  active && payload && payload.length ? (
                    <div className="rounded bg-gray-900 px-2 py-1 text-xs text-white">
                      <div>{(payload[0].payload as { range: string }).range}</div>
                      <div>{payload[0].value} calls</div>
                    </div>
                  ) : null
                }
              />
              <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* By-capability breakdown (collapsible) */}
      {data.by_capability.length > 0 && (
        <div className="rounded-lg border border-gray-200">
          <button
            type="button"
            onClick={() => setShowCapability((v) => !v)}
            className="flex w-full items-center justify-between rounded-t-lg bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            <span>By capability over this range ({data.by_capability.length})</span>
            <span className="text-xs text-indigo-600">{showCapability ? 'Hide' : 'Show'}</span>
          </button>
          {showCapability && (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Capability
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Total Cost
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Calls
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Avg/Call
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.by_capability.map((cap) => (
                  <tr key={cap.capability}>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {cap.capability.replace(/_/g, ' ')}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-700">
                      {formatCost(cap.total_cost)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">{cap.call_count}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-700">
                      {formatCost(cap.avg_cost_per_call, 6)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function MarginTab({ margin }: { margin: MarginResponse }) {
  const marginPercent = parseFloat(margin.gross_margin_percent);
  const marginColor =
    marginPercent >= 70 ? 'text-green-600' : marginPercent >= 40 ? 'text-amber-600' : 'text-red-600';
  const barColor =
    marginPercent >= 70 ? 'bg-green-500' : marginPercent >= 40 ? 'bg-amber-500' : 'bg-red-500';

  const hasData = parseFloat(margin.revenue.total || '0') > 0 || parseFloat(margin.cost.total || '0') > 0;

  if (!hasData) {
    return (
      <div className="text-center py-8 text-gray-500">
        <CurrencyDollarIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm">No margin data for this period.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Margin headline */}
      <div className="flex items-center gap-4">
        <div>
          <div className="text-sm text-gray-500">Gross Margin</div>
          <div className={`text-2xl font-bold ${marginColor}`}>
            ${margin.gross_margin}{' '}
            <span className="text-lg">({margin.gross_margin_percent}%)</span>
          </div>
        </div>
        <div className="flex-1">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full ${barColor}`}
              style={{ width: `${Math.min(Math.max(marginPercent, 0), 100)}%` }}
            />
          </div>
        </div>
      </div>

      {marginPercent < 40 && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5" />
          Low margin — review pricing or cost structure
        </div>
      )}

      {/* Side-by-side revenue vs cost */}
      <div className="grid grid-cols-2 gap-6">
        {/* Revenue column */}
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wider">
            Revenue
          </h4>
          <dl className="space-y-1.5">
            {Object.entries(margin.revenue)
              .filter(([k]) => k !== 'total')
              .map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <dt className="text-gray-600 capitalize">{key}</dt>
                  <dd className="font-mono text-gray-900">{formatCost(value, 2)}</dd>
                </div>
              ))}
            <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-1.5">
              <dt className="text-gray-900">Total</dt>
              <dd className="font-mono text-gray-900">
                {formatCost(margin.revenue.total || '0', 2)}
              </dd>
            </div>
          </dl>
        </div>

        {/* Cost column */}
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wider">
            Cost
          </h4>
          <dl className="space-y-1.5">
            {Object.entries(margin.cost)
              .filter(([k]) => k !== 'total')
              .map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <dt className="text-gray-600">{key.replace(/_/g, ' ')}</dt>
                  <dd className="font-mono text-gray-900">{formatCost(value, 2)}</dd>
                </div>
              ))}
            <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-1.5">
              <dt className="text-gray-900">Total</dt>
              <dd className="font-mono text-gray-900">
                {formatCost(margin.cost.total || '0', 2)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
